/**
 * Session store facade used by every activity bundle.
 *
 * Two stores hold the in-progress session data for a uid: Rails
 * (/api/v1/active_activity_sessions) and Supabase (see sessionServiceApi.ts).
 * Every save is written to both. Which one is *primary* — awaited, and read
 * from — is decided per page load by window.postbuildEnv.SUPABASE_SESSION_PRIMARY,
 * which Rails computes from the `supabase_session_primary` AppSetting
 * (staff / percentage / allow-list). The other store is the fallback:
 *
 *   get:    Supabase-primary — read Supabase; on a miss, a failure, or after
 *           PRIMARY_WAIT_MS, read Rails; rejects { status: 404 } only when
 *           Rails also has nothing. Rails-primary — read Rails and trust its
 *           404 (consulting the Supabase copy instead could resurrect a
 *           session Rails already deleted on completion); fall back to
 *           Supabase only when Rails errors. Callers use the 404 rejection to
 *           start a fresh session.
 *   update: write both at once; resolve once the primary succeeds AND the
 *           secondary has settled (or PRIMARY_WAIT_MS passed) — callers
 *           navigate on resolution, and abandoning an in-flight secondary
 *           write would leave that store stale across a rollout flag change.
 *           If the primary fails or times out, resolve if the secondary
 *           succeeded; reject only if both fail.
 *
 * Every fallback taken is reported to Sentry (tag service:session-store) so the
 * rollout can be watched. See app/services/supabase/README.md.
 */
import * as Sentry from '@sentry/browser';

import { SessionServiceApi, SessionServiceSuperseded } from './sessionServiceApi';
import { requestGet, requestPut } from '../../../modules/request/index';

const sessionApiBaseUrl = `${window.postbuildEnv.DEFAULT_URL}/api/v1/active_activity_sessions`;

// How long a caller waits on the primary store before the fallback kicks in.
// The primary request keeps running in the background (a slow write can still
// land; retries and supersession are handled in sessionServiceApi), but the
// student-facing promise moves on so a Supabase outage cannot hang session
// resume or completion for the full retry cycle.
const PRIMARY_WAIT_MS = 4000;
const TIMED_OUT = {};

function raceWithTimeout<T>(promise: Promise<T>, ms: number): Promise<T | typeof TIMED_OUT> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => resolve(TIMED_OUT), ms);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (error) => { clearTimeout(timer); reject(error); }
    );
  });
}

type Store = 'supabase' | 'rails';
type Op = 'get' | 'update';

type SessionNotFound = { status: 404 };

const notFound = (): SessionNotFound => ({ status: 404 });
const isNotFound = (e: any): boolean => e && e.status === 404;

function primaryStore(): Store {
  const env = (window.postbuildEnv || {}) as any;
  // The availability check keeps a misconfigured client (flag on but URL/key
  // missing) from being chosen as primary: SessionServiceApi resolves no-ops
  // in that state, which must never be read as a durable save.
  return env.SUPABASE_SERVICE_ENABLED && env.SUPABASE_SESSION_PRIMARY && SessionServiceApi.available()
    ? 'supabase'
    : 'rails';
}

// One report per op+store per interval: during an outage every active student
// falls back on every request, and the Supabase failures themselves are
// already reported by sessionServiceApi — this is a health signal, not a
// per-event log.
const FALLBACK_REPORT_INTERVAL_MS = 60_000;
let lastFallbackReportAt: { [key: string]: number } = {};

function reportFallback(op: Op, from: Store, to: Store, reason: any): void {
  const key = `${op}:${from}`;
  const now = Date.now();
  if (lastFallbackReportAt[key] && now - lastFallbackReportAt[key] < FALLBACK_REPORT_INTERVAL_MS) return;
  lastFallbackReportAt[key] = now;

  Sentry.captureMessage('session-store fallback', {
    level: Sentry.Severity.Warning,
    tags: { service: 'session-store', op, primary: from, fallback_to: to },
    extra: { reason: reason instanceof Error ? reason.message : reason },
  });
}

// The throttle above is module state; tests reset it between cases.
function resetSessionStoreReportingForTests(): void {
  lastFallbackReportAt = {};
}

// --- Rails leg ---------------------------------------------------------------
// requestGet/requestPut resolve with the parsed body; on a non-2xx response the
// error callback receives { ...body, status } and we rethrow it so the promise
// rejects with that same object (callers check error.status === 404).

const rails = {
  get(uid: string): Promise<object> {
    return requestGet(`${sessionApiBaseUrl}/${uid}.json`, null, (error) => { throw error; });
  },
  update(uid: string, data: object): Promise<object> {
    return requestPut(`${sessionApiBaseUrl}/${uid}.json`, { active_activity_session: data }, null, (error) => { throw error; });
  },
};

// --- Supabase leg ------------------------------------------------------------
// SessionServiceApi.get resolves null when there is no row (it does not reject),
// so callers of this leg must treat null as "not here".

const supabase = {
  get(uid: string): Promise<object | null> {
    return SessionServiceApi.get(uid);
  },
  update(uid: string, data: object): Promise<void> {
    return SessionServiceApi.update(uid, data);
  },
};

// --- Facade ------------------------------------------------------------------

class SessionApi {
  static get(uid: string): Promise<object> {
    return primaryStore() === 'supabase' ? getSupabaseFirst(uid) : getRailsFirst(uid);
  }

  static update(uid: string, data: object): Promise<object | void> {
    const primary = primaryStore();
    const railsWrite = rails.update(uid, data);
    // An unavailable Supabase client resolves without writing; represent it as
    // a rejection so the fallback logic cannot count it as a successful store.
    //
    // A superseded write is the opposite: a newer save for this session has
    // taken over and carries everything this one did, so treat it as success.
    // Without this, a superseded save whose Rails leg also failed would reject
    // the caller's promise (surfacing as an unhandled SessionServiceSuperseded)
    // and could be mislabeled as a store fallback.
    const supabaseWrite = SessionServiceApi.available()
      ? supabase.update(uid, data).catch((error) => {
        if (error instanceof SessionServiceSuperseded) return;
        throw error;
      })
      : Promise.reject(new Error('Supabase session client unavailable'));

    const [first, second, secondStore]: [Promise<any>, Promise<any>, Store] =
      primary === 'supabase'
        ? [supabaseWrite, railsWrite, 'rails']
        : [railsWrite, supabaseWrite, 'supabase'];

    // Keep the secondary's rejection from surfacing as unhandled when the
    // primary succeeds. Supabase failures are already reported inside
    // sessionServiceApi; Rails failures are reported here.
    second.catch((error) => {
      if (secondStore === 'rails') {
        Sentry.captureException(toError(error), { tags: { service: 'session-store', op: 'update', store: 'rails' } });
      }
    });

    first.catch(() => {}); // consumed below; never an unhandled rejection

    return (async () => {
      try {
        const result = await raceWithTimeout(first, PRIMARY_WAIT_MS);
        if (result !== TIMED_OUT) {
          // Primary accepted. Give the secondary a bounded window to settle
          // before reporting success: callers may navigate immediately on
          // resolution (e.g. Proofreader completion), which would abort an
          // in-flight secondary fetch and silently leave that store behind —
          // surfacing as lost progress if the rollout flag later moves the
          // user onto it.
          await raceWithTimeout(second.catch(() => {}), PRIMARY_WAIT_MS);
          return result;
        }

        reportFallback('update', primary, secondStore, 'primary timed out');
        try {
          return await second;
        } catch {
          return await first; // secondary failed; wait out the primary after all
        }
      } catch (primaryError) {
        reportFallback('update', primary, secondStore, primaryError);
        return second.catch(() => { throw primaryError; });
      }
    })();
  }
}

async function getSupabaseFirst(uid: string): Promise<object> {
  const primaryRead = supabase.get(uid);
  primaryRead.catch(() => {}); // may be abandoned by the timeout below

  try {
    const result = await raceWithTimeout(primaryRead, PRIMARY_WAIT_MS);
    if (result === TIMED_OUT) {
      reportFallback('get', 'supabase', 'rails', 'primary timed out');
    } else if (result) {
      return result as object;
    }
    // A Supabase miss (null) is not proof the session is new: a Rails copy may
    // exist (e.g. a save whose Supabase write failed). Only a Rails 404
    // confirms it, so fall through without reporting.
  } catch (error) {
    reportFallback('get', 'supabase', 'rails', error);
  }

  try {
    return await rails.get(uid);
  } catch (error) {
    if (isNotFound(error)) throw notFound();
    throw error;
  }
}

async function getRailsFirst(uid: string): Promise<object> {
  // Shadow read keeps Supabase exercised under Rails-primary; also serves as
  // the fallback below without a second request.
  const shadow = supabase.get(uid);
  shadow.catch(() => {});

  try {
    return await rails.get(uid);
  } catch (error) {
    // Trust a Rails 404 without consulting the shadow read: Rails is the store
    // of record in this mode, and a stale Supabase row (e.g. a delete still in
    // flight after activity completion) must not resurrect the session.
    if (isNotFound(error)) throw notFound();
    reportFallback('get', 'rails', 'supabase', error);
    const data = await shadow.catch(() => null);
    if (data) return data;
    throw error;
  }
}

function toError(e: any): Error {
  if (e instanceof Error) return e;
  const err = new Error(`Rails session request failed${e && e.status ? ` (${e.status})` : ''}`);
  (err as any).status = e && e.status;
  return err;
}

export {
  SessionApi,
  sessionApiBaseUrl,
  resetSessionStoreReportingForTests
};
